import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

/**
 * Hook for managing Transport Fees
 *
 * Provides functions to:
 * - Fetch all transport fees with filters
 * - Fetch outstanding transport fees
 * - Fetch student's transport fees
 * - Get complete student transport fee details
 */
export const useTransportFees = () => {
  const navigate = useNavigate();
  const [transportFees, setTransportFees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchTransportFees = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {
          page: params.page || pagination.current,
          pageSize: params.pageSize || pagination.pageSize,
        };

        // Only add filters that have valid values
        if (params.filters) {
          Object.entries(params.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              queryObj[key] = value;
            }
          });
        }

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.finance.transportFees}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          const data = response.data || response;
          const fees = Array.isArray(data) ? data : data.results || [];
          setTransportFees(fees);
          const total = data.count !== undefined ? data.count : fees.length;
          setPagination((prev) => ({ ...prev, total }));
        } else {
          Utilities.showError(response, "Failed to fetch transport fees");
        }
      } catch (error) {
        message.error("An error occurred while fetching transport fees");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate, pagination.current, pagination.pageSize],
  );

  const fetchOutstandingTransportFees = useCallback(async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.finance.transportFeesOutstanding,
        null,
        navigate,
      );

      if (response.ok) {
        return Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];
      } else {
        Utilities.showError(
          response,
          "Failed to fetch outstanding transport fees",
        );
        return [];
      }
    } catch (error) {
      console.error("Error fetching outstanding transport fees:", error);
      message.error(
        "An error occurred while fetching outstanding transport fees",
      );
      return [];
    }
  }, [navigate]);

  const fetchStudentTransportFees = useCallback(
    async (spc) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "GET",
          domainSettings.urls.finance.studentTransportFees.replace(":spc", spc),
          null,
          navigate,
        );

        if (response.ok) {
          return Array.isArray(response.data) ? response.data : [];
        } else {
          Utilities.showError(
            response,
            "Failed to fetch student transport fees",
          );
          return [];
        }
      } catch (error) {
        console.error("Error fetching student transport fees:", error);
        message.error(
          "An error occurred while fetching student transport fees",
        );
        return [];
      }
    },
    [navigate],
  );

  const fetchStudentTransportFeeDetails = useCallback(
    async (spc) => {
      try {
        const transportFeesResponse = await Utilities.fetchWithAuth(
          "GET",
          domainSettings.urls.finance.studentTransportFees.replace(":spc", spc),
          null,
          navigate,
        );

        if (!transportFeesResponse.ok) {
          return {
            student: { spc },
            transportFees: {
              items: [],
              count: 0,
              totalDue: 0,
              totalPaid: 0,
              balance: 0,
            },
          };
        }

        const fees = Array.isArray(transportFeesResponse.data)
          ? transportFeesResponse.data
          : [];

        const totals = fees.reduce(
          (acc, fee) => {
            acc.totalDue += parseFloat(fee.amountDue || 0);
            acc.totalPaid += parseFloat(fee.amountPaid || 0);
            acc.balance += parseFloat(fee.balance || 0);
            return acc;
          },
          { totalDue: 0, totalPaid: 0, balance: 0 },
        );

        return {
          student: { spc },
          transportFees: {
            items: fees,
            count: fees.length,
            ...totals,
          },
        };
      } catch (error) {
        console.error("Error fetching student transport fee details:", error);
        return {
          student: { spc },
          transportFees: {
            items: [],
            count: 0,
            totalDue: 0,
            totalPaid: 0,
            balance: 0,
          },
        };
      }
    },
    [navigate],
  );

  return {
    transportFees,
    loading,
    pagination,
    setPagination,
    fetchTransportFees,
    fetchOutstandingTransportFees,
    fetchStudentTransportFees,
    fetchStudentTransportFeeDetails,
  };
};
